import React from 'react';
import styled from 'styled-components';

const PreviewContainer = styled.div`
  width: 100%;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const PreviewSection = styled.div`
  padding: 40px 20px;
  border-bottom: 1px solid #eee;
  position: relative;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover .edit-overlay {
    opacity: 1;
  }
  
  /* Remove padding and border for sections with full-width containers */
  &.no-border {
    padding: 0;
    border-bottom: none;
  }
`;

const EditOverlay = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  gap: 8px;
  opacity: 0;
  transition: opacity 0.2s;
  z-index: 10;
`;

const EditButton = styled.button`
  padding: 8px 12px;
  background: rgba(59, 130, 246, 0.9);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &:hover {
    background: rgba(37, 99, 235, 0.9);
  }
`;

const DeleteButton = styled.button`
  padding: 8px 12px;
  background: rgba(239, 68, 68, 0.9);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  
  &:hover {
    background: rgba(220, 38, 38, 0.9);
  }
`;

const HeroSection = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
`;

const EventTitle = styled.h1`
  font-size: 48px;
  margin-bottom: 16px;
  font-weight: 700;
`;

const EventDescription = styled.p`
  font-size: 18px;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;
`;

const PosterImage = styled.img`
  width: 100%;
  max-width: 800px;
  height: auto;
  margin: 20px auto;
  display: block;
  border-radius: 8px;
`;

// Fazyra Touch Section
const FazyraTouchSection = styled.div`
  padding: 60px 20px;
  background: #f9fafb;
`;

const SectionTitle = styled.h2`
  font-size: 36px;
  text-align: center;
  margin-bottom: 12px;
  color: #1f2937;
`;

const SectionSubtitle = styled.p`
  font-size: 18px;
  text-align: center;
  color: #6b7280;
  margin-bottom: 40px;
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  max-width: 1200px;
  margin: 0 auto 40px;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 250px;
  object-fit: cover;
  border-radius: 8px;
`;

const DescriptionText = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: #4b5563;
  max-width: 800px;
  margin: 20px auto;
  text-align: center;
`;

const FeaturesList = styled.div`
  max-width: 800px;
  margin: 30px auto;
`;

const FeatureItem = styled.div`
  margin-bottom: 20px;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const FeatureLabel = styled.h4`
  font-size: 18px;
  color: #1f2937;
  margin-bottom: 8px;
  font-weight: 600;
`;

const FeatureDescription = styled.p`
  font-size: 14px;
  color: #6b7280;
  line-height: 1.6;
`;

// Highlights Section
const HighlightsSection = styled.div`
  padding: 60px 20px;
  background: white;
`;

const HighlightsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const HighlightCard = styled.div`
  padding: 30px;
  background: #f9fafb;
  border-radius: 12px;
  text-align: center;
  transition: transform 0.2s;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const CardIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const CardHeading = styled.h3`
  font-size: 20px;
  color: #1f2937;
  margin-bottom: 12px;
  font-weight: 600;
`;

const CardDescription = styled.p`
  font-size: 14px;
  color: #6b7280;
  line-height: 1.6;
`;

// Help You Plan Section
const HelpYouPlanSection = styled.div`
  padding: 60px 20px;
  background: white;
`;

const VideoContainer = styled.div`
  position: relative;
  max-width: 1400px;
  width: 100%;
  height: 600px;
  margin: 0 auto;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  @media (max-width: 768px) {
    height: 500px;
    flex-direction: column;
  }
`;

const VideoWrapper = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  width: 65%;
  height: 100%;
  overflow: hidden;
  z-index: 1;
  isolation: isolate; /* Create new stacking context */
  
  /* White gradient overlay from left */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 55%;
    height: 100%;
    background: linear-gradient(to right, 
      rgba(255, 255, 255, 1) 0%,
      rgba(255, 255, 255, 1) 8%,
      rgba(255, 255, 255, 0.98) 15%,
      rgba(255, 255, 255, 0.9) 25%,
      rgba(255, 255, 255, 0.75) 40%,
      rgba(255, 255, 255, 0.5) 60%,
      rgba(255, 255, 255, 0.2) 80%,
      rgba(255, 255, 255, 0) 100%
    );
    z-index: 2;
    pointer-events: none;
  }
  
  /* Bottom gradient overlay */
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 40%;
    background: linear-gradient(to top,
      rgba(255, 255, 255, 0.95) 0%,
      rgba(255, 255, 255, 0.7) 30%,
      rgba(255, 255, 255, 0) 100%
    );
    z-index: 2;
    pointer-events: none;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    height: 50%;
  }
`;

const VideoPreview = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const VideoContent = styled.div`
  position: relative;
  width: 45%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 60px;
  z-index: 2;
  
  @media (max-width: 768px) {
    width: 100%;
    height: 50%;
    padding: 30px;
  }
`;

const VideoTitle = styled.h3`
  font-size: 28px;
  color: #1f2937;
  margin-bottom: 12px;
  font-weight: 600;
`;

const VideoSubtitle = styled.p`
  font-size: 16px;
  color: #6b7280;
  line-height: 1.6;
`;

const CardGridSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  max-width: 1200px;
  margin: 40px auto 0;
`;

const PlanCard = styled.div`
  padding: 20px;
  background: white;
  border-radius: 8px;
  cursor: pointer;
  border: 2px solid ${props => props.active ? '#3b82f6' : 'transparent'};
  transition: all 0.2s;
  
  &:hover {
    border-color: #3b82f6;
    transform: translateY(-2px);
  }
`;

const PlanCardName = styled.h4`
  font-size: 16px;
  color: #1f2937;
  margin-bottom: 8px;
  font-weight: 600;
`;

const PlanCardDescription = styled.p`
  font-size: 14px;
  color: #6b7280;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #9ca3af;
`;

// Dynamic Sections - Card Left Layout
const DynamicSection = styled.div`
  display: flex;
  align-items: center;
  gap: 40px;
  padding: 60px 40px;
  position: relative;
  
  @media (max-width: 768px) {
    flex-direction: column;
    padding: 40px 20px;
  }
`;

const DynamicCardLeft = styled.div`
  flex: 0 0 45%;
  z-index: 2;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  padding: 40px;
  
  @media (max-width: 768px) {
    flex: 1;
    width: 100%;
  }
`;

const DynamicImageRight = styled.div`
  flex: 1;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  
  img {
    width: 100%;
    height: 500px;
    object-fit: cover;
    display: block;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    
    img {
      height: 300px;
    }
  }
`;

// Dynamic Sections - Card Right Layout (Reversed)
const DynamicSectionReversed = styled.div`
  display: flex;
  align-items: center;
  gap: 40px;
  padding: 60px 40px;
  position: relative;
  
  @media (max-width: 768px) {
    flex-direction: column-reverse;
    padding: 40px 20px;
  }
`;

const DynamicImageLeft = styled.div`
  flex: 1;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  
  img {
    width: 100%;
    height: 500px;
    object-fit: cover;
    display: block;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    
    img {
      height: 300px;
    }
  }
`;

const DynamicCardRight = styled.div`
  flex: 0 0 45%;
  z-index: 2;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  padding: 40px;
  
  @media (max-width: 768px) {
    flex: 1;
    width: 100%;
  }
`;

const DynamicCardInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DynamicTitle = styled.h3`
  font-size: 28px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const DynamicParagraph = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: #555;
  margin: 0;
`;

const DynamicFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 16px 0 0 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DynamicFeature = styled.li`
  font-size: 15px;
  color: #333;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EventPreview = ({ eventData, onEdit, onDelete }) => {
  const [activeVideoIndex, setActiveVideoIndex] = React.useState(0);

  // Use environment variable for API URL - works in both dev and production
  const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

  // Log when eventData changes (for debugging)
  React.useEffect(() => {
    if (eventData.fazyraTouch) {
      console.log('🎨 EventPreview received Fazyra Touch data:', {
        title: eventData.fazyraTouch.title,
        image1: eventData.fazyraTouch.image1 || eventData.fazyraTouch.image1Preview,
        image2: eventData.fazyraTouch.image2 || eventData.fazyraTouch.image2Preview,
        image3: eventData.fazyraTouch.image3 || eventData.fazyraTouch.image3Preview,
        mainDescription: eventData.fazyraTouch.mainDescription?.substring(0, 50),
        features: eventData.fazyraTouch.features?.length
      });
    }
    if (eventData.highlights) {
      console.log('✨ EventPreview received Highlights data:', {
        title: eventData.highlights.title,
        enabled: eventData.highlights.enabled,
        cardsCount: eventData.highlights.cards?.length,
        cards: eventData.highlights.cards?.map(c => ({ icon: c.icon, heading: c.heading }))
      });
    }
  }, [eventData.fazyraTouch, eventData.highlights]);

  const getImageUrl = (url) => {
    if (!url) return '';
    return url.startsWith('http') ? url : `${API_BASE}${url}`;
  };

  const getVideoUrl = (url) => {
    if (!url) return '';
    return url.startsWith('http') ? url : `${API_BASE}${url}`;
  };

  return (
    <PreviewContainer>
      {/* Hero Section */}
      <PreviewSection>
        <EditOverlay className="edit-overlay">
          <EditButton onClick={() => onEdit('basic')}>✏️ Edit</EditButton>
        </EditOverlay>
        <HeroSection>
          <EventTitle>{eventData.name || 'Event Name'}</EventTitle>
          <EventDescription>{eventData.description || 'Event description goes here...'}</EventDescription>
          {eventData.posterImage && (
            <PosterImage src={getImageUrl(eventData.posterImage)} alt={eventData.name} />
          )}
        </HeroSection>
      </PreviewSection>

      {/* Fazyra Touch Section */}
      {eventData.fazyraTouch && eventData.fazyraTouch.enabled && (
        <PreviewSection>
          <EditOverlay className="edit-overlay">
            <EditButton onClick={() => onEdit('fazyraTouch')}>✏️ Edit</EditButton>
            <DeleteButton onClick={() => onDelete('fazyraTouch')}>🗑️ Remove</DeleteButton>
          </EditOverlay>
          <FazyraTouchSection>
            <SectionTitle>{eventData.fazyraTouch.title}</SectionTitle>
            <SectionSubtitle>{eventData.fazyraTouch.subtitle}</SectionSubtitle>
            
            <ImageGrid>
              {(eventData.fazyraTouch.image1 || eventData.fazyraTouch.image1Preview) && (
                <PreviewImage 
                  key={`fazyra-img1-${eventData.fazyraTouch.image1 || eventData.fazyraTouch.image1Preview}`}
                  src={getImageUrl(eventData.fazyraTouch.image1 || eventData.fazyraTouch.image1Preview)} 
                  alt={eventData.fazyraTouch.image1Alt} 
                />
              )}
              {(eventData.fazyraTouch.image2 || eventData.fazyraTouch.image2Preview) && (
                <PreviewImage 
                  key={`fazyra-img2-${eventData.fazyraTouch.image2 || eventData.fazyraTouch.image2Preview}`}
                  src={getImageUrl(eventData.fazyraTouch.image2 || eventData.fazyraTouch.image2Preview)} 
                  alt={eventData.fazyraTouch.image2Alt} 
                />
              )}
              {(eventData.fazyraTouch.image3 || eventData.fazyraTouch.image3Preview) && (
                <PreviewImage 
                  key={`fazyra-img3-${eventData.fazyraTouch.image3 || eventData.fazyraTouch.image3Preview}`}
                  src={getImageUrl(eventData.fazyraTouch.image3 || eventData.fazyraTouch.image3Preview)} 
                  alt={eventData.fazyraTouch.image3Alt} 
                />
              )}
            </ImageGrid>

            {eventData.fazyraTouch.mainDescription && (
              <DescriptionText>{eventData.fazyraTouch.mainDescription}</DescriptionText>
            )}

            {eventData.fazyraTouch.features && eventData.fazyraTouch.features.length > 0 && (
              <FeaturesList>
                {eventData.fazyraTouch.features.filter(f => f.label || f.description).map((feature, idx) => (
                  <FeatureItem key={`${feature.label}-${idx}`}>
                    <FeatureLabel>{feature.label}</FeatureLabel>
                    <FeatureDescription>{feature.description}</FeatureDescription>
                  </FeatureItem>
                ))}
              </FeaturesList>
            )}

            {eventData.fazyraTouch.closingDescription && (
              <DescriptionText>{eventData.fazyraTouch.closingDescription}</DescriptionText>
            )}
          </FazyraTouchSection>
        </PreviewSection>
      )}

      {/* Highlights Section */}
      {eventData.highlights && eventData.highlights.enabled && eventData.highlights.cards && eventData.highlights.cards.length > 0 && (
        <PreviewSection>
          <EditOverlay className="edit-overlay">
            <EditButton onClick={() => onEdit('highlights')}>✏️ Edit</EditButton>
            <DeleteButton onClick={() => onDelete('highlights')}>🗑️ Remove</DeleteButton>
          </EditOverlay>
          <HighlightsSection>
            <SectionTitle>{eventData.highlights.title}</SectionTitle>
            <SectionSubtitle>{eventData.highlights.subtitle}</SectionSubtitle>
            
            <HighlightsGrid>
              {eventData.highlights.cards.map((card, idx) => (
                <HighlightCard key={`highlight-${idx}-${card.heading}-${card.icon}`}>
                  <CardIcon>{card.icon}</CardIcon>
                  <CardHeading>{card.heading}</CardHeading>
                  <CardDescription>{card.description}</CardDescription>
                </HighlightCard>
              ))}
            </HighlightsGrid>
          </HighlightsSection>
        </PreviewSection>
      )}

      {/* Help You Plan Section */}
      {eventData.helpYouPlan && eventData.helpYouPlan.enabled && eventData.helpYouPlan.cards && eventData.helpYouPlan.cards.length > 0 && (
        <PreviewSection>
          <EditOverlay className="edit-overlay">
            <EditButton onClick={() => onEdit('helpYouPlan')}>✏️ Edit</EditButton>
            <DeleteButton onClick={() => onDelete('helpYouPlan')}>🗑️ Remove</DeleteButton>
          </EditOverlay>
          <HelpYouPlanSection>
            <SectionTitle>{eventData.helpYouPlan.sectionHeading}</SectionTitle>
            
            {console.log('🎥 Help You Plan - Total cards:', eventData.helpYouPlan.cards.length)}
            {console.log('🎥 Help You Plan - Active video index:', activeVideoIndex)}
            {eventData.helpYouPlan.cards.map((card, idx) => 
              console.log(`🎥 Card ${idx}: ${card.cardName} - Video: ${card.videoUrl}`)
            )}
            
            <VideoContainer>
              {eventData.helpYouPlan.cards[activeVideoIndex] && (
                <>
                  {console.log(`🎬 Rendering video for card ${activeVideoIndex}: ${eventData.helpYouPlan.cards[activeVideoIndex].videoUrl}`)}
                  {console.log(`🎬 Final video URL: ${getVideoUrl(eventData.helpYouPlan.cards[activeVideoIndex].videoUrl)}`)}
                  <VideoContent>
                    <VideoTitle>{eventData.helpYouPlan.cards[activeVideoIndex].videoTitle}</VideoTitle>
                    <VideoSubtitle>{eventData.helpYouPlan.cards[activeVideoIndex].videoSubtitle}</VideoSubtitle>
                  </VideoContent>
                  <VideoWrapper>
                    <VideoPreview 
                      key={`video-${activeVideoIndex}-${eventData.helpYouPlan.cards[activeVideoIndex].videoUrl}`}
                      autoPlay 
                      muted 
                      loop 
                      playsInline
                    >
                      <source src={getVideoUrl(eventData.helpYouPlan.cards[activeVideoIndex].videoUrl)} type="video/mp4" />
                    </VideoPreview>
                  </VideoWrapper>
                </>
              )}
            </VideoContainer>

            <CardGridSection>
              {eventData.helpYouPlan.cards.map((card, idx) => (
                <PlanCard 
                  key={idx} 
                  active={idx === activeVideoIndex}
                  onClick={() => setActiveVideoIndex(idx)}
                >
                  <PlanCardName>{card.cardName}</PlanCardName>
                  <PlanCardDescription>{card.cardDescription}</PlanCardDescription>
                </PlanCard>
              ))}
            </CardGridSection>
          </HelpYouPlanSection>
        </PreviewSection>
      )}

      {/* Dynamic Sections */}
      {eventData.dynamicSections && eventData.dynamicSections.length > 0 && (
        <>
          {console.log('EventPreview - Total dynamic sections:', eventData.dynamicSections.length)}
          {console.log('EventPreview - Dynamic sections data:', eventData.dynamicSections)}
          {eventData.dynamicSections
            .filter(section => section.enabled !== false)
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map((section, index) => {
              console.log(`Rendering section ${index}:`, section);
              const imageSource = section.imagePreview || 
                                 (section.imageUrl ? getImageUrl(section.imageUrl) : '') || 
                                 'https://via.placeholder.com/800x600?text=No+Image';
              console.log(`Section ${index} image source:`, imageSource);
              return (
            <PreviewSection key={index} className="no-border">
              <EditOverlay className="edit-overlay">
                <EditButton onClick={() => onEdit('dynamicSections')}>
                  ✏️ Edit Section
                </EditButton>
              </EditOverlay>
              
              {section.layout === 'card-right' ? (
                /* Card Right Layout - Image Left, Card Right */
                <DynamicSectionReversed>
                  <DynamicImageLeft>
                    <img 
                      src={imageSource} 
                      alt={section.imageAlt || 'Section image'} 
                      onError={(e) => {
                        console.error(`Image load error for section ${index}`);
                        e.target.src = 'https://via.placeholder.com/800x600?text=Image+Not+Found';
                      }}
                    />
                  </DynamicImageLeft>
                  <DynamicCardRight>
                    <DynamicCardInner>
                      <DynamicTitle>{section.title}</DynamicTitle>
                      <DynamicParagraph>{section.paragraph1}</DynamicParagraph>
                      {section.paragraph2 && <DynamicParagraph>{section.paragraph2}</DynamicParagraph>}
                      {section.features && section.features.filter(f => f && f.trim()).length > 0 && (
                        <DynamicFeatures>
                          {section.features.filter(f => f && f.trim()).map((feature, idx) => (
                            <DynamicFeature key={idx}>⭐ {feature}</DynamicFeature>
                          ))}
                        </DynamicFeatures>
                      )}
                    </DynamicCardInner>
                  </DynamicCardRight>
                </DynamicSectionReversed>
              ) : (
                /* Card Left Layout - Card Left, Image Right */
                <DynamicSection>
                  <DynamicCardLeft>
                    <DynamicCardInner>
                      <DynamicTitle>{section.title}</DynamicTitle>
                      <DynamicParagraph>{section.paragraph1}</DynamicParagraph>
                      {section.paragraph2 && <DynamicParagraph>{section.paragraph2}</DynamicParagraph>}
                      {section.features && section.features.filter(f => f && f.trim()).length > 0 && (
                        <DynamicFeatures>
                          {section.features.filter(f => f && f.trim()).map((feature, idx) => (
                            <DynamicFeature key={idx}>⭐ {feature}</DynamicFeature>
                          ))}
                        </DynamicFeatures>
                      )}
                    </DynamicCardInner>
                  </DynamicCardLeft>
                  <DynamicImageRight>
                    <img 
                      src={imageSource} 
                      alt={section.imageAlt || 'Section image'} 
                      onError={(e) => {
                        console.error(`Image load error for section ${index}`);
                        e.target.src = 'https://via.placeholder.com/800x600?text=Image+Not+Found';
                      }}
                    />
                  </DynamicImageRight>
                </DynamicSection>
              )}
            </PreviewSection>
              );
            })
          }
        </>
      )}

      {/* Empty State */}
      {!eventData.fazyraTouch?.enabled && 
       !eventData.highlights?.enabled && 
       !eventData.helpYouPlan?.enabled && 
       (!eventData.dynamicSections || eventData.dynamicSections.length === 0) && (
        <EmptyState>
          <p>No sections added yet. Click "+ Add Section" to start building your event page.</p>
        </EmptyState>
      )}
    </PreviewContainer>
  );
};

export default EventPreview;
